import {
  CircularProgress,
  Navbar,
  NavbarLayout,
  PermissionCode,
  SidebarLayout,
  i18nCore,
  useHasPermission,
  useIsFetching,
  useIsInitLoading,
  useUser,
} from '@core-frontend/core-frontend';
import type {
  NavbarMenuItem,
  SidebarMenuItem,
  SidebarMenuSection,
} from '@core-frontend/core-frontend';
import {
  AdminPanelSettings as AdminPanelSettingsIcon,
  ApartmentOutlined as ApartmentOutlinedIcon,
  AssignmentIndOutlined as AssignmentIndOutlinedIcon, // ContactMail as ContactMailIcon,
  CorporateFare as CorporateFareIcon,
  Dns as DnsIcon, // Extension as ExtensionIcon,
  // Info as InfoIcon,
  // Key as KeyIcon,
  HistoryEdu as HistoryEduIcon,
  ManageAccounts as ManageAccountsIcon, // Payments as PaymentsIcon,
  People as PeopleIcon,
  Person as PersonIcon, // Receipt as ReceiptIcon,
  // RequestQuote as RequestQuoteIcon,
  // Storage as StorageIcon,
  // Subscriptions as SubscriptionsIcon,
  // Update as UpdateIcon,
  // Widgets as WidgetsIcon,
} from '@mui/icons-material';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Outlet, type To, useLocation, useNavigate } from 'react-router-dom';

import { AppRoutes } from '@/app/app-router';

import { LogoutButton } from '@/entities/session';

import { ProbitLogo } from './logo';

const AuthenticatedLayoutWithSidebar = () => {
  const userInfo = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoading = useIsInitLoading();
  const isFetching = useIsFetching();
  const { hasPermission } = useHasPermission();
  const { t } = useTranslation('navMenu');

  const renderMenuItemsWithPermissions = (
    permission: PermissionCode,
    menuItem: SidebarMenuItem
  ): SidebarMenuItem | null => {
    return hasPermission(permission) ? menuItem : null;
  };

  const navbarMenuItems: NavbarMenuItem[] = [
    // {
    //   title: 'Users & Permissions',
    //   onClick: () => navigate(AppRoutes.USERS),
    //   isActive: location.pathname.includes(AppRoutes.USERS),
    // },
    // {
    //   title: 'Forms',
    //   onClick: () => navigate(AppRoutes.FORMS),
    //   isActive: location.pathname.includes(AppRoutes.FORMS),
    // },
    {
      title: t('Navbar.Administration', { defaultValue: 'ADMINISTRATION' }),
      onClick: () => navigate(AppRoutes.HOME),
      isActive:
        location.pathname.includes(AppRoutes.HOME) &&
        !location.pathname.includes(AppRoutes.HRM),
    },
    {
      title: t('Navbar.HRM', { defaultValue: 'HRM' }),
      onClick: () => navigate(AppRoutes.HRM),
      isActive: location.pathname.includes(AppRoutes.HRM),
    },
    // {
    //   title: 'Billing',
    //   onClick: () => console.log('Billing'),
    // },
    // {
    //   title: 'Settings',
    //   onClick: () => console.log('Settings'),
    // },
    // {
    //   title: 'Products',
    //   onClick: () => console.log('Products'),
    // },
  ];

  const sidebarSections: SidebarMenuSection[] = [
    {
      title: t('Sidebar.Administration.UsersAndPermissions', {
        defaultValue: 'Users and Permissions',
      }),
      icon: <AssignmentIndOutlinedIcon />,
      subSectionItems: [
        renderMenuItemsWithPermissions(PermissionCode.VIEW_USERS, {
          title: t('Sidebar.Administration.UserInvitations', {
            defaultValue: 'User Invitations',
          }),
          route: AppRoutes.USER_INVITATIONS,
          icon: <PersonIcon />,
          isActive: location.pathname.includes(AppRoutes.USER_INVITATIONS),
        }),
        renderMenuItemsWithPermissions(PermissionCode.VIEW_ROLES, {
          title: t('Sidebar.Administration.Roles', { defaultValue: 'Roles' }),
          icon: <PeopleIcon />,
          route: AppRoutes.ROLES,
          isActive: location.pathname.includes(AppRoutes.ROLES),
        }),
        // {
        //   title: 'Permissions',
        //   onClick: () => navigate(AppRoutes.PERMISSIONS_EDIT),
        //   icon: <AdminPanelSettingsIcon />,
        // },
        renderMenuItemsWithPermissions(PermissionCode.VIEW_USERS, {
          title: t('Sidebar.Administration.ManagedAccounts', {
            defaultValue: 'Managed Accounts',
          }),
          route: AppRoutes.USER_ACCOUNTS,
          icon: <ManageAccountsIcon />,
          isActive: location.pathname.includes(AppRoutes.USER_ACCOUNTS),
        }),
      ].filter((item): item is SidebarMenuItem => item !== null),
    },
    {
      title: t('Sidebar.Administration.Organizations', {
        defaultValue: 'Organizations',
      }),
      icon: <CorporateFareIcon />,
      subSectionItems: [
        renderMenuItemsWithPermissions(PermissionCode.VIEW_ORGANIZATION, {
          title: t('Sidebar.Administration.Organizations', {
            defaultValue: 'Organizations',
          }),
          route: AppRoutes.ORGANIZATIONS,
          icon: <ApartmentOutlinedIcon />,
        }),
      ].filter((item): item is SidebarMenuItem => item !== null),
    },
    {
      title: t('Sidebar.Administration.Forms', { defaultValue: 'Forms' }),
      icon: <HistoryEduIcon />,
      subSectionItems: [
        {
          title: t('Sidebar.Administration.Forms', { defaultValue: 'Forms' }),
          route: AppRoutes.FORMS,
          icon: <HistoryEduIcon />,
          isActive: location.pathname.includes(AppRoutes.FORMS),
        },
      ],
    },
  ];

  if (window.isCurrentHostEqualsCore) {
    sidebarSections.push({
      title: t('Sidebar.Administration.Settings', {
        defaultValue: 'Settings',
      }),
      subSectionItems: [
        // {
        //   title: 'Details',
        //   onClick: () => console.log('Details'),
        //   icon: <InfoIcon />,
        // },
        renderMenuItemsWithPermissions(PermissionCode.VIEW_DOMAIN, {
          title: t('Sidebar.Administration.Domains', {
            defaultValue: 'Domains',
          }),
          route: AppRoutes.DOMAINS,
          icon: <DnsIcon />,
        }),
        // {
        //   title: 'API keys',
        //   onClick: () => console.log('API keys'),
        //   icon: <KeyIcon />,
        // },
      ].filter((item): item is SidebarMenuItem => item !== null),
    });
  }

  return (
    <NavbarLayout
      companyName="PRO-BIT"
      logoIcon={ProbitLogo}
      menuItems={navbarMenuItems}
      isLoading={isLoading || isFetching}
      profileMenu={
        <Navbar.Profile>
          <Navbar.ProfileInfo
            title={`${userInfo?.user?.first_name} ${userInfo?.user?.last_name}`}
            subtitle={userInfo?.user?.email}
            onClick={() => navigate(AppRoutes.USER_ME)}
          />
          <Navbar.ProfileDivider />
          <Navbar.ProfileMenuButton
            sx={{
              width: '100%',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              px: 2,
              gap: 1,
              textTransform: 'none',
            }}
            variant="text"
            onClick={() => navigate(AppRoutes.PERMISSIONS_MY)}
          >
            <AdminPanelSettingsIcon fontSize="small" />
            {t('ProfileMenu.MyPermissions', {
              defaultValue: 'My permissions',
            })}
          </Navbar.ProfileMenuButton>
          <Navbar.ProfileMenuButton
            onClick={() => {
              i18nCore.changeLanguage('en-US');
            }}
          >
            ENGLISH
          </Navbar.ProfileMenuButton>
          <Navbar.ProfileMenuButton
            onClick={() => {
              i18nCore.changeLanguage('sl-SI');
            }}
          >
            SLO
          </Navbar.ProfileMenuButton>
          <Navbar.ProfileDivider />
          <LogoutButton />
        </Navbar.Profile>
      }
    >
      <SidebarLayout
        isLoading={isLoading || isFetching}
        withTopbar
        handleNavigate={(route) => navigate(route as To)}
        sections={sidebarSections}
      >
        {isLoading || isFetching ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: 'calc(100vh - 64px)',
            }}
          >
            <CircularProgress size={240} indicatorColor="#EBEBEB" />
          </Box>
        ) : (
          <Outlet />
        )}
      </SidebarLayout>
    </NavbarLayout>
  );
};

export default AuthenticatedLayoutWithSidebar;
